import "pages/App.css";
import "react-toastify/dist/ReactToastify.min.css";
import "utils/i18n";

import { BrowserRouter, Redirect, Route } from "react-router-dom";
import React, { Component, Suspense } from "react";
import { appStage, nodeEnv } from "environment";

import AnalyticsBlock from "pages/AnalyticsBlock";
import Error404 from "components/general/Error404";
import { ErrorTest } from "pages/ErrorTest";
import FragmentSupportingSwitch from "components/router/FragmentSupportingSwitch";
import GlobalStyle from "styles/globalStyle";
import { PrivateRoute } from "components/router/PrivateRoute";
import { PuckToastContainer } from "components/general/Toast";
import { ShowEnv } from "pages/ShowEnv";
import { Unauthorized } from "pages/auth/Unauthorized";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import importRetry from "utils/importRetry";
import { isLoggedIn } from "utils/api";
import { keepSessionAlive } from "slices/employers";

/* PUCK ADMIN PAGES */
const Impersonate = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "admin" */
      "pages/auth/Impersonate"
    )
  )
);

/* AUTH PAGES */
const ClaimAccount = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "auth" */
      "pages/auth/ClaimAccount"
    )
  )
);
const ClaimAccountSignup = React.lazy(() =>
  import(
    /* webpackChunkName: "auth" */
    "pages/auth/ClaimAccountSignup"
  )
);
const ClaimAccountProfile = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "auth" */
      "pages/auth/ClaimAccountProfile"
    )
  )
);
const ForgotPassword = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "auth" */
      "pages/auth/ForgotPassword"
    )
  )
);
const ResetPassword = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "auth" */
      "pages/auth/ResetPassword"
    )
  )
);
const Login = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "auth" */
      "pages/auth/Login"
    )
  )
);
const Logout = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "auth" */
      "pages/auth/Logout"
    )
  )
);

/* DASHBOARD PAGES */
const Home = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/Home"
    )
  )
);
const DashboardJobClipEdit = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/JobClipEdit"
    )
  )
);
const DashboardJobPages = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/JobPages"
    )
  )
);
const DashboardLeads = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/Leads"
    )
  )
);
const DashboardScreeningQuestions = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/ScreeningQuestions"
    )
  )
);
const DashboardScreeningQuestionsEdit = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/ScreeningQuestionsEdit"
    )
  )
);
const DashboardTextToApply = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/TextToApply"
    )
  )
);
const DashboardPodcasts = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/Podcasts"
    )
  )
);
const DashboardSettingsProfile = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/settings/Profile"
    )
  )
);
const DashboardSettingsAts = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/settings/Ats"
    )
  )
);
const DashboardSettingsMembers = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/settings/Members"
    )
  )
);
const DashboardSettingsSmartLinks = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/settings/SmartLinks"
    )
  )
);
const DashboardSettingsApplicationReminders = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/settings/ApplicationReminders"
    )
  )
);
const DashboardSmartLinks = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/SmartLinks"
    )
  )
);
const DashboardTeaserEdit = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/TeaserEdit"
    )
  )
);
const DashboardTeasers = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/Teasers"
    )
  )
);
const LeverCallback = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/ats/LeverCallback"
    )
  )
);
const NativeSocialShare = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/share/NativeSocialShare"
    )
  )
);
const ContentPages = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/ContentPages"
    )
  )
);
const ContentPageEdit = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/ContentPageEdit"
    )
  )
);
const SourcingPageWizard = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/SourcingPageWizard"
    )
  )
);
const SourcingPagePreview = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "dashboard" */
      "pages/dashboard/SourcingPagePreview"
    )
  )
);

/* PUBLIC CANDIDATE PAGES */
const JobBoardPublic = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "job-board-public" */
      "pages/jobs/JobBoardPublic"
    )
  )
);
const JobPagePublic = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "job-page-public" */
      "pages/jobs/JobPagePublic"
    )
  )
);
const JobPageApplicationPublic = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "job-page-application-public" */
      "pages/jobs/JobPageApplicationPublic"
    )
  )
);
const JobClipPublic = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "job-clip-public" */
      "pages/jobs/JobClipPublic"
    )
  )
);
const JobClipTranscriptPublic = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "job-clip-transcript-public" */
      "pages/jobs/JobClipTranscriptPublic"
    )
  )
);
const AppReminderOptOut = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "app-reminder-opt-out" */
      "pages/jobs/AppReminderOptOut"
    )
  )
);
const SalaryCalculatorResults = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "salary-calculator" */
      "pages/salary_calculator/Results"
    )
  )
);
const SalaryCalculatorResultsLinked = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "salary-calculator" */
      "pages/salary_calculator/ResultsLinked"
    )
  )
);
const ContentPagePublic = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "content-page-public" */
      "pages/content_pages/ContentPagePublic"
    )
  )
);
const ContentPagePublicSales = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "content-page-public-sales" */
      "pages/content_pages/ContentPagePublicSales"
    )
  )
);
/* EMBED PAGE */
const Embed = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "embed" */
      "pages/embed/Embed"
    )
  )
);
/* PDF REPORT DOWNLOAD PAGE */
const PdfReportDownload = React.lazy(() =>
  importRetry(() =>
    import(
      /* webpackChunkName: "pdf-reports" */
      "pages/pdf_reports/Download"
    )
  )
);

class App extends Component {
  // Send a keep-alive ping to the server
  componentDidMount() {
    // 25m timer
    this.keepAliveInterval = setInterval(() => {
      this.keepAlive();
    }, 25 * 60 * 1000);
  }

  componentWillUnmount() {
    // Probably not necessary, but can't hurt
    clearInterval(this.keepAliveInterval);
  }

  keepAlive() {
    const { user, keepSessionAlive } = this.props;

    if (isLoggedIn() && user?.employerId) {
      keepSessionAlive(user.employerId);
    }
  }

  render() {
    return (
      <>
        <GlobalStyle />
        <PuckToastContainer />
        <BrowserRouter>
          <Suspense fallback={null}>
            <FragmentSupportingSwitch>
              <PrivateRoute
                exact
                path="/auth/lever/callback"
                component={LeverCallback}
              />
              <Route path="/login">
                <Redirect to="/employers/login" />
              </Route>
              <Route path="/employers/login" component={Login} />
              <Route path="/employers/claim-account" component={ClaimAccount} />
              <Route
                path="/employers/claim-account-signup"
                component={ClaimAccountSignup}
              />
              <Route
                path="/employers/claim-account-profile"
                component={ClaimAccountProfile}
              />
              <Route
                path="/employers/forgot-password"
                component={ForgotPassword}
              />
              <Route
                path="/employers/reset-password"
                component={ResetPassword}
              />
              <Route path="/logout" component={Logout} />
              <Route path="/analytics/block" component={AnalyticsBlock} />
              <PrivateRoute path="/impersonate" component={Impersonate} />
              <PrivateRoute path={["/", "/employers"]} component={Home} exact />
              {/* Embed */}
              <Route
                path={[
                  "/job-clip/:jobClipPermalink/track/:jobClipTrackPermalink/embed",
                  "/job-clip/:jobClipPermalink/embed",
                ]}
                component={Embed}
              />
              {/* Native Share Flows */}
              <Route
                path={[
                  "/job-clip/:jobClipPermalink/native-social-share",
                  "/job-board/:jobBoardPermalink/job/:jobPagePermalink/native-social-share",
                ]}
                component={NativeSocialShare}
              />
              {/* Employer Job Clips */}
              <Route
                path="/job-clip/:jobClipPermalink/transcript"
                component={JobClipTranscriptPublic}
              />
              <Route
                path="/job-clip/:jobClipPermalink"
                component={JobClipPublic}
              />
              <Route
                path="/job-board/:jobBoardPermalink/job/:jobPagePermalink/apply"
                component={JobPageApplicationPublic}
              />
              <Route
                path="/job-board/:jobBoardPermalink/job/:jobPagePermalink"
                component={JobPagePublic}
              />
              <Route
                path="/job-board/:jobBoardPermalink"
                component={JobBoardPublic}
              />
              <Route
                path="/page/:contentPagePermalink"
                component={ContentPagePublic}
              />
              <Route
                path="/sales/:employerPermalink/page/:contentPagePermalink"
                component={ContentPagePublicSales}
              />
              <PrivateRoute
                path={["/employers/podcast", "/employers/podcast/"]}
                component={DashboardPodcasts}
                exact
              />
              <PrivateRoute
                path={["/employers/job-page", "/employers/job-page/"]}
                component={DashboardJobPages}
                exact
              />
              <PrivateRoute
                path={["/employers/leads/screening-questions/new"]}
                component={DashboardScreeningQuestionsEdit}
              />
              <PrivateRoute
                path={[
                  "/employers/leads/screening-questions/:screeningSetPermalink/edit",
                ]}
                component={DashboardScreeningQuestionsEdit}
              />
              <PrivateRoute
                path={["/employers/leads/screening-questions"]}
                component={DashboardScreeningQuestions}
              />
              <PrivateRoute
                path={["/employers/leads", "/employers/leads/"]}
                component={DashboardLeads}
                exact
              />
              <PrivateRoute
                path={["/employers/text-to-apply", "/employers/text-to-apply/"]}
                component={DashboardTextToApply}
                exact
              />
              <PrivateRoute
                path={["/employers/smart-link"]}
                component={DashboardSmartLinks}
                exact
              />
              <PrivateRoute
                path={["/employers/video-clip/edit"]}
                component={DashboardTeaserEdit}
                exact
              />
              <PrivateRoute
                path={["/employers/video-clip"]}
                component={DashboardTeasers}
                exact
              />
              <PrivateRoute
                path={[
                  "/employers/settings/profile",
                  "/employers/settings/profile/",
                ]}
                component={DashboardSettingsProfile}
                exact
              />
              <PrivateRoute
                path={["/employers/settings/ats", "/employers/settings/ats/"]}
                component={DashboardSettingsAts}
                exact
              />
              <PrivateRoute
                path={[
                  "/employers/settings/members",
                  "/employers/settings/members/",
                ]}
                component={DashboardSettingsMembers}
                exact
              />
              <PrivateRoute
                path={[
                  "/employers/settings/smart-links",
                  "/employers/settings/smart-links/",
                ]}
                component={DashboardSettingsSmartLinks}
                exact
              />
              <PrivateRoute
                path={[
                  "/employers/settings/application-reminders",
                  "/employers/settings/application-reminders/",
                ]}
                component={DashboardSettingsApplicationReminders}
                exact
              />
              <PrivateRoute
                path={["/employers/settings", "/employers/settings/"]}
                exact
              >
                <Redirect to="/employers/settings/ats" />
              </PrivateRoute>
              <PrivateRoute
                path="/employers/podcast/job-clip/:jobClipPermalink/edit"
                component={DashboardJobClipEdit}
              />
              <PrivateRoute
                path="/employers/page/sourcing-page-wizard"
                component={SourcingPageWizard}
              />
              <PrivateRoute
                path="/employers/page/:contentPagePermalink/preview"
                component={SourcingPagePreview}
              />
              <PrivateRoute
                path="/employers/page/:contentPagePermalink/edit"
                component={ContentPageEdit}
              />
              <PrivateRoute path="/employers/page" component={ContentPages} />
              <PrivateRoute
                path="/employers/:employerPermalink/pdf-report/:pdfReportPermalink/download"
                component={PdfReportDownload}
              />
              <Route
                path="/app-reminder/opt-out/:sequenceTargetPermalink"
                component={AppReminderOptOut}
              />
              <Route
                path="/salary-calculator/link/:permalink"
                component={SalaryCalculatorResultsLinked}
              />
              <Route
                path="/salary-calculator"
                component={SalaryCalculatorResults}
              />
              {(appStage === "uat" || nodeEnv === "development") && (
                <>
                  <Route path="/__env" component={ShowEnv} />
                  <Route path="/__error" component={ErrorTest} />
                  <Route path="/__unauthorized" component={Unauthorized} />
                </>
              )}
              <Route path="/404" component={Error404} />
              <Route component={Error404} />
            </FragmentSupportingSwitch>
          </Suspense>
        </BrowserRouter>
      </>
    );
  }
}

function mapStateToProps(state) {
  const { auth } = state;
  return {
    user: auth.user,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    ...bindActionCreators(
      {
        keepSessionAlive,
      },
      dispatch
    ),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(App);
